import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// jalaali-js removed in favor of centralized calendar utilities
import { syncStorage } from '@/lib/chromeStorageAdapter'
import { useHabitStore, useWeeklyStore, useTasksStore, useFinanceStore, useSettingsStore } from '@/store'
import { Card, Button, LoadingSpinner, EmptyState } from '@/components/ui'
import { getConsistentGradient } from '@/lib/gradientColors'
import { formatTimeInTimezone } from '@/lib/timezoneUtils'
import { fetchWeather, getWeatherIcon, type WeatherForecast } from '@/lib/weatherUtils'
import { formatDateLocalized, formatDualDates, englishToFarsiNumber } from '@/lib/calendar'
import { motivationalQuotes } from '@/data/quotes'
import { persianPoems } from '@/data/persianPoems'
import QuickNotes from '@/components/dashboard/QuickNotes'
import PersianPoetry from '@/components/dashboard/PersianPoetry'

export interface Bookmark {
  id: string
  title: string
  url: string
  imageUrl?: string
}

const DEFAULT_BOOKMARKS: Bookmark[] = [
  { id: '1', title: 'Sadatpour', url: 'https://sadatpour.ir', imageUrl: '/bookmarks/sadatpour.jpg' },
  { id: '2', title: 'Gmail', url: 'https://mail.google.com' },
  { id: '3', title: 'GitHub', url: 'https://github.com' },
  { id: '4', title: 'YouTube', url: 'https://youtube.com' },
]

export default function DashboardNew() {
  const { t } = useTranslation()
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(DEFAULT_BOOKMARKS)
  const [isLoaded, setIsLoaded] = useState(false)
  const [showBookmarkForm, setShowBookmarkForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [bookmarkTitle, setBookmarkTitle] = useState('')
  const [bookmarkUrl, setBookmarkUrl] = useState('')
  const [bookmarkImage, setBookmarkImage] = useState<string | null>(null)
  const [, setClockUpdate] = useState(0) // Force re-render for clock updates
  const [weatherData, setWeatherData] = useState<Record<string, WeatherForecast | null>>({})
  
  // New state for tools
  const [currentQuote, setCurrentQuote] = useState(motivationalQuotes[0])
  const [currentPoem, setCurrentPoem] = useState(persianPoems[0])
  const [quickNotes, setQuickNotes] = useState<string[]>([])
  const [pomodoroTime, setPomodoroTime] = useState(() => {
    const saved = localStorage.getItem('pomodoroState')
    if (saved) {
      try {
        const state = JSON.parse(saved)
        return state.time || 25 * 60
      } catch {}
    }
    return 25 * 60
  })
  const [pomodoroRunning, setPomodoroRunning] = useState(() => {
    const saved = localStorage.getItem('pomodoroState')
    if (saved) {
      try {
        const state = JSON.parse(saved)
        return state.running || false
      } catch {}
    }
    return false
  })
  const [pomodoroDisplay, setPomodoroDisplay] = useState('25:00')
  const [pomodoroSessionCount, setPomodoroSessionCount] = useState(() => {
    const saved = localStorage.getItem('pomodoroState')
    if (saved) {
      try {
        const state = JSON.parse(saved)
        return state.sessionCount || 0
      } catch {}
    }
    return 0
  })
  const [pomodoroIsBreak, setPomodoroIsBreak] = useState(() => {
    const saved = localStorage.getItem('pomodoroState')
    if (saved) {
      try {
        const state = JSON.parse(saved)
        return state.isBreak || false
      } catch {}
    }
    return false
  })
  const [showPoemAnalysis, setShowPoemAnalysis] = useState(false)

  // Store selectors
  const habits = useHabitStore(state => state.habits)
  const weeks = useWeeklyStore(state => state.weeks)
  const tasks = useTasksStore(state => state.tasks)
  const getTotalIncome = useFinanceStore(state => state.getTotalIncome)
  const getTotalExpense = useFinanceStore(state => state.getTotalExpense)
  const clockLocations = useSettingsStore(state => state.clockLocations)
  const weatherLocations = useSettingsStore(state => state.weatherLocations)
  const plannerName = useSettingsStore(state => state.plannerName)
  const language = useSettingsStore(state => state.language)
  const calendar = useSettingsStore(state => state.calendar)

  // Update clocks every second
  useEffect(() => {
    const interval = setInterval(() => {
      setClockUpdate(prev => prev + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Request notification permission
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Play sound effect
  const playNotificationSound = useCallback(() => {
    try {
      // Use Web Audio API to create a beep sound
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const now = audioContext.currentTime
      
      // Create multiple beeps for alarm effect
      for (let i = 0; i < 3; i++) {
        const osc = audioContext.createOscillator()
        const gain = audioContext.createGain()
        
        osc.connect(gain)
        gain.connect(audioContext.destination)
        
        osc.frequency.value = 1000 + i * 200 // Increasing frequency
        osc.type = 'sine'
        
        gain.gain.setValueAtTime(0.3, now + i * 0.3)
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.3 + 0.2)
        
        osc.start(now + i * 0.3)
        osc.stop(now + i * 0.3 + 0.2)
      }
    } catch (e) {
      console.error('صوت پخش نشد:', e)
    }
  }, [])

  // Pomodoro Timer
  useEffect(() => {
    if (!pomodoroRunning) return

    const interval = setInterval(() => {
      setPomodoroTime((prev) => {
        if (prev <= 1) {
          setPomodoroRunning(false)
          
          // Play notification sound
          playNotificationSound()
          
          // Show notification
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            const title = pomodoroIsBreak
              ? language === 'fa'
                ? '✅ وقت کار شروع شد!'
                : '✅ Time to Work!'
              : language === 'fa'
              ? '☕ وقت استراحت شروع شد!'
              : '☕ Break Time Started!'
            
            const options = {
              icon: pomodoroIsBreak ? '💪' : '☕',
              tag: 'pomodoro',
              badge: pomodoroIsBreak ? '💪' : '☕',
              body: pomodoroIsBreak
                ? language === 'fa'
                  ? 'بیرون رفتید استراحت کنید'
                  : 'Take a break'
                : language === 'fa'
                ? 'بیایید کار کنیم'
                : 'Let\'s work!',
            }
            new Notification(title, options)
          }
          
          // تغییر حالت
          if (!pomodoroIsBreak) {
            setPomodoroSessionCount((prev) => prev + 1)
            setPomodoroIsBreak(true)
            setPomodoroTime(5 * 60)
          } else {
            setPomodoroIsBreak(false)
            setPomodoroTime(25 * 60)
          }
          
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [pomodoroRunning, pomodoroIsBreak, language, playNotificationSound])

  // Update pomodoro display
  useEffect(() => {
    const mins = Math.floor(pomodoroTime / 60)
    const secs = pomodoroTime % 60
    setPomodoroDisplay(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`)
  }, [pomodoroTime])

  // Save pomodoro state to localStorage
  useEffect(() => {
    localStorage.setItem('pomodoroState', JSON.stringify({
      time: pomodoroTime,
      running: pomodoroRunning,
      sessionCount: pomodoroSessionCount,
      isBreak: pomodoroIsBreak,
    }))
  }, [pomodoroTime, pomodoroRunning, pomodoroSessionCount, pomodoroIsBreak])

  // Load quick notes and initialize random quote/poem
  useEffect(() => {
    // Load quick notes
    const savedNotes = localStorage.getItem('quickNotes')
    if (savedNotes) {
      setQuickNotes(JSON.parse(savedNotes))
    }
    
    // Initialize random quote and poem
    const randomQuoteIndex = Math.floor(Math.random() * motivationalQuotes.length)
    const randomPoemIndex = Math.floor(Math.random() * persianPoems.length)
    setCurrentQuote(motivationalQuotes[randomQuoteIndex])
    setCurrentPoem(persianPoems[randomPoemIndex])
  }, [])

  // Fetch weather data
  useEffect(() => {
    const fetchAllWeather = async () => {
      const newWeatherData: Record<string, WeatherForecast | null> = {}
      
      for (const location of weatherLocations) {
        try {
          const data = await fetchWeather(location.latitude, location.longitude, location.city)
          newWeatherData[location.id] = data
        } catch (err) {
          console.error(`Failed to fetch weather for ${location.city}:`, err)
          newWeatherData[location.id] = null
        }
      }
      
      setWeatherData(newWeatherData)
    }

    if (weatherLocations.length > 0) {
      fetchAllWeather()
      // Refresh weather every 30 minutes
      const interval = setInterval(fetchAllWeather, 30 * 60 * 1000)
      return () => clearInterval(interval)
    }
  }, [weatherLocations])

  // Load bookmarks on mount
  useEffect(() => {
    const loadBookmarks = async () => {
      try {
        const cached = syncStorage.get('bookmarks') as Bookmark[] | undefined
        if (cached && Array.isArray(cached) && cached.length > 0) {
          setBookmarks(cached)
        } else {
          setBookmarks(DEFAULT_BOOKMARKS)
        }
      } catch (err) {
        console.error('❌ Failed to load bookmarks:', err)
        setBookmarks(DEFAULT_BOOKMARKS)
      } finally {
        setIsLoaded(true)
      }
    }

    loadBookmarks()
  }, [])

  // Calculate statistics
  const statistics = useMemo(() => {
    // Debug: log all habits from store
    console.log('📊 ALL HABITS from store:', {
      isArray: Array.isArray(habits),
      length: Array.isArray(habits) ? habits.length : 0,
      rawData: habits
    });
    
    // Filter valid habits (with startDate and endDate)
    const validHabits = Array.isArray(habits) ? habits.filter(h => {
      const isValid = h.startDate && h.endDate && 
             !isNaN(new Date(h.startDate).getTime()) && 
             !isNaN(new Date(h.endDate).getTime())
      if (!isValid) {
        console.log('⚠️ Filtered out habit (invalid dates):', h);
      }
      return isValid
    }) : []
    
    const habitCount = validHabits.length
    
    // Debug log
    console.log('🔍 DEBUG Habits:', {
      totalCount: Array.isArray(habits) ? habits.length : 0,
      validCount: habitCount,
      habits: validHabits.map(h => ({
        id: h.id,
        title: h.title,
        completions: Array.isArray(h?.completions) ? h.completions.length : 0,
        startDate: h.startDate,
        endDate: h.endDate
      }))
    });
    
    const completedHabits = validHabits.filter(h => {
      const completions = Array.isArray(h?.completions) ? h.completions : []
      return completions.length > 0
    }).length

    // Weekly tasks stats - safe access
    const weeklyTasksList = weeks && Array.isArray(weeks)
      ? weeks
          .flatMap(w => (w.days ? Object.values(w.days) : []))
          .flatMap(dayTasks => dayTasks || [])
      : []
    const completedWeeklyTasks = weeklyTasksList.filter(t => t?.done).length

    // Tasks stats
    const taskCount = Array.isArray(tasks) ? tasks.length : 0
    const completedTasks = Array.isArray(tasks) ? tasks.filter(t => t?.done).length : 0

    // Finance stats
    const income = getTotalIncome()
    const expense = getTotalExpense()
    const balance = income - expense

    return [
      {
        id: 'habits',
        icon: '✅',
        title: t('habit.title', 'عادات'),
        value: habitCount, // show total count, not completed count
        total: habitCount,
        percentage: habitCount > 0 ? Math.round((completedHabits / habitCount) * 100) : 0,
        bgColor: 'from-purple-500 to-pink-500',
      },
      {
        id: 'weekly',
        icon: '📅',
        title: t('weekly.title', 'برنامه هفتگی'),
        value: completedWeeklyTasks,
        total: weeklyTasksList.length,
        percentage: weeklyTasksList.length > 0 ? Math.round((completedWeeklyTasks / weeklyTasksList.length) * 100) : 0,
        bgColor: 'from-blue-500 to-cyan-500',
      },
      {
        id: 'tasks',
        icon: '📝',
        title: t('tasks.title', 'تسک‌ها'),
        value: completedTasks,
        total: taskCount,
        percentage: taskCount > 0 ? Math.round((completedTasks / taskCount) * 100) : 0,
        bgColor: 'from-green-500 to-emerald-500',
      },
      {
        id: 'finance',
        icon: '💰',
        title: t('finance.title', 'دارایی'),
        value: Math.round(balance),
        total: Math.round(income),
        percentage: income > 0 ? Math.round((balance / income) * 100) : 0,
        bgColor: 'from-yellow-500 to-orange-500',
      },
    ]
  }, [habits, weeks, tasks, getTotalIncome, getTotalExpense, t])

  // Handle add/update bookmark
  const handleSaveBookmark = useCallback(() => {
    if (!bookmarkUrl.trim()) {
      alert('لطفاً URL را وارد کنید')
      return
    }

    let updated: Bookmark[]
    
    if (editingId) {
      // Update existing bookmark
      updated = bookmarks.map(b =>
        b.id === editingId
          ? {
              ...b,
              title: bookmarkTitle.trim() || new URL(bookmarkUrl).hostname,
              url: bookmarkUrl.trim(),
              imageUrl: bookmarkImage || undefined,
            }
          : b
      )
    } else {
      // Add new bookmark
      const newBookmark: Bookmark = {
        id: `bookmark-${Date.now()}`,
        title: bookmarkTitle.trim() || new URL(bookmarkUrl).hostname,
        url: bookmarkUrl.trim(),
        imageUrl: bookmarkImage || undefined,
      }
      updated = [...bookmarks, newBookmark]
    }

    setBookmarks(updated)
    syncStorage.set('bookmarks', updated)
    
    // Reset form
    resetBookmarkForm()
  }, [bookmarks, bookmarkTitle, bookmarkUrl, bookmarkImage, editingId])

  // Handle edit bookmark
  const handleEditBookmark = useCallback((id: string) => {
    const bookmark = bookmarks.find(b => b.id === id)
    if (bookmark) {
      setEditingId(id)
      setBookmarkTitle(bookmark.title)
      setBookmarkUrl(bookmark.url)
      setBookmarkImage(bookmark.imageUrl || null)
      setShowBookmarkForm(true)
    }
  }, [bookmarks])

  // Reset form
  const resetBookmarkForm = useCallback(() => {
    setBookmarkTitle('')
    setBookmarkUrl('')
    setBookmarkImage(null)
    setEditingId(null)
    setShowBookmarkForm(false)
  }, [])

  // Handle delete bookmark
  const handleDeleteBookmark = useCallback((id: string) => {
    const updated = bookmarks.filter(b => b.id !== id)
    setBookmarks(updated)
    syncStorage.set('bookmarks', updated)
  }, [bookmarks])

  // Get favicon URL
  const getFaviconUrl = (url: string): string => {
    try {
      const domain = new URL(url).hostname
      return `https://www.google.com/s2/favicons?sz=64&domain=${domain}`
    } catch {
      return ''
    }
  }

  // تاریخ‌های کنونی را بر اساس تقویم انتخاب‌شده محاسبه کن
  const getFormattedDates = () => {
    const today = new Date()
    const dates = formatDualDates(today, language === 'fa' ? 'fa' : 'en')
    return dates
  }

  const dates = useMemo(() => getFormattedDates(), [language, calendar])

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center flex-col">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600 dark:text-gray-400 mt-4">{t('common.loading', 'در حال بارگذاری...')}</p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 py-0 border-b border-gray-200 dark:border-gray-700 px-4 pt-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">📌 {plannerName}</h1>
          </div>

          {/* Date + Clock Cards Section - on the left */}
          <div className="flex items-stretch gap-3">
            {/* Date Card */}
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-4 shadow-md text-white flex-shrink-0" style={{ maxHeight: '110px', minWidth: '200px' }}>
              <p className="text-sm font-semibold opacity-90 mb-2">تاریخ امروز</p>
              {calendar === 'jalali' ? (
                <>
                  <p className="text-2xl font-bold leading-tight">{language === 'fa' ? englishToFarsiNumber(dates.jalali) : dates.jalali}</p>
                  <p className="text-xs opacity-75 mt-2">{dates.gregorian}</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold leading-tight">{dates.gregorian}</p>
                  <p className="text-xs opacity-75 mt-2">{language === 'fa' ? englishToFarsiNumber(dates.jalali) : dates.jalali}</p>
                </>
              )}
            </div>

            {/* Clock Cards - Horizontal Scroll */}
            {clockLocations.length > 0 && (
              <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'thin', maxWidth: '500px' } as React.CSSProperties}>
                {clockLocations.map((clock) => {
                  const gradient = getConsistentGradient(clock.id)
                  const time = formatTimeInTimezone(clock.timezone)
                  return (
                    <div
                      key={clock.id}
                      className={`flex-shrink-0 bg-gradient-to-br ${gradient} rounded-lg p-4 text-white shadow-md text-center flex flex-col justify-center`}
                      style={{ minWidth: '110px', maxHeight: '110px' }}
                    >
                      <p className="text-xs font-semibold opacity-90 mb-2 truncate">{clock.city}</p>
                      <p className="text-lg font-bold leading-tight">{time}</p>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Weather Cards - Horizontal Scroll */}
        {/* Removed from header - only shown in weather forecast section below */}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scrollbar-custom space-y-6 p-4">
        
        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {statistics.map(stat => {
            // Special rendering for each stat type
            if (stat.id === 'habits') {
              const validHabits = Array.isArray(habits) ? habits.filter(h => {
                return h.startDate && h.endDate && 
                       !isNaN(new Date(h.startDate).getTime()) && 
                       !isNaN(new Date(h.endDate).getTime())
              }) : []
              const completedHabits = validHabits.filter(h => {
                const completions = Array.isArray(h?.completions) ? h.completions : []
                return completions.length > 0
              }).length

              return (
                <Card key={stat.id} className="p-5 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 opacity-5 group-hover:opacity-10 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-4xl">✅</div>
                      <div className="text-right flex-1">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">عادات روزانه</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {language === 'fa' ? englishToFarsiNumber(completedHabits) : completedHabits}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400">درحال</span>
                        <span className="font-bold text-gray-700 dark:text-gray-300">{language === 'fa' ? englishToFarsiNumber(stat.total) : stat.total}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" style={{width: `${stat.percentage}%`}}></div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-right mt-2">
                        {language === 'fa' ? englishToFarsiNumber(stat.percentage) : stat.percentage}% فعال
                      </p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-600 dark:text-gray-400">تراند: 
                        <span className="ml-1 font-bold text-green-600 dark:text-green-400">📈 صعودی</span>
                      </p>
                    </div>
                  </div>
                </Card>
              )
            }

            if (stat.id === 'weekly') {
              const weeklyTasksList = weeks && Array.isArray(weeks)
                ? weeks.flatMap(w => (w.days ? Object.values(w.days) : [])).flatMap(dayTasks => dayTasks || [])
                : []
              const todayTasks = weeklyTasksList.slice(0, Math.max(3, Math.floor(weeklyTasksList.length / 2)))

              return (
                <Card key={stat.id} className="p-5 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5 group-hover:opacity-10 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-4xl">📅</div>
                      <div className="text-right flex-1">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">برنامه هفتگی</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {language === 'fa' ? englishToFarsiNumber(stat.value) : stat.value}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400">کل وظایف</span>
                        <span className="font-bold text-gray-700 dark:text-gray-300">{language === 'fa' ? englishToFarsiNumber(stat.total) : stat.total}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500" style={{width: `${stat.percentage}%`}}></div>
                      </div>
                    </div>
                    <div className="space-y-1 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">وظایف فعال:</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        🎯 {language === 'fa' ? englishToFarsiNumber(todayTasks.length) : todayTasks.length} کار اساسی
                      </p>
                    </div>
                  </div>
                </Card>
              )
            }

            if (stat.id === 'tasks') {
              const taskStats = {
                high: Array.isArray(tasks) ? tasks.filter(t => t.priority === 'high').length : 0,
                medium: Array.isArray(tasks) ? tasks.filter(t => t.priority === 'medium').length : 0,
                low: Array.isArray(tasks) ? tasks.filter(t => t.priority === 'low').length : 0,
              }

              return (
                <Card key={stat.id} className="p-5 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 opacity-5 group-hover:opacity-10 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-4xl">📝</div>
                      <div className="text-right flex-1">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">تسک‌ها</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {language === 'fa' ? englishToFarsiNumber(stat.value) : stat.value}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400">از {language === 'fa' ? englishToFarsiNumber(stat.total) : stat.total}</span>
                        <span className="font-bold text-gray-700 dark:text-gray-300">{language === 'fa' ? englishToFarsiNumber(stat.percentage) : stat.percentage}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500" style={{width: `${stat.percentage}%`}}></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <p className="text-lg font-bold text-red-600 dark:text-red-400">{language === 'fa' ? englishToFarsiNumber(taskStats.high) : taskStats.high}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">فوری</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{language === 'fa' ? englishToFarsiNumber(taskStats.medium) : taskStats.medium}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">متوسط</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{language === 'fa' ? englishToFarsiNumber(taskStats.low) : taskStats.low}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">عادی</p>
                      </div>
                    </div>
                  </div>
                </Card>
              )
            }

            if (stat.id === 'finance') {
              return (
                <Card key={stat.id} className="p-5 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-orange-500 opacity-5 group-hover:opacity-10 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-4xl">💰</div>
                      <div className="text-right flex-1">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">مدیریت مالی</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {language === 'fa' ? englishToFarsiNumber(Math.abs(stat.value)) : Math.abs(stat.value)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-green-100 dark:bg-green-900/30 rounded p-2">
                        <p className="text-xs text-green-700 dark:text-green-300 font-semibold">درآمد</p>
                        <p className="text-sm font-bold text-green-700 dark:text-green-400">
                          {language === 'fa' ? englishToFarsiNumber(stat.total) : stat.total}
                        </p>
                      </div>
                      <div className="bg-red-100 dark:bg-red-900/30 rounded p-2">
                        <p className="text-xs text-red-700 dark:text-red-300 font-semibold">هزینه</p>
                        <p className="text-sm font-bold text-red-700 dark:text-red-400">
                          {language === 'fa' ? englishToFarsiNumber(stat.total - stat.value) : stat.total - stat.value}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${stat.value >= 0 ? 'from-green-500 to-emerald-500' : 'from-red-500 to-orange-500'}`}
                          style={{ width: `${Math.min(100, Math.abs(stat.percentage))}%` }}
                        ></div>
                      </div>
                      <p className={`text-xs font-semibold ${stat.value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {stat.value >= 0 ? '📈 مثبت' : '📉 منفی'}
                      </p>
                    </div>
                  </div>
                </Card>
              )
            }
          })}
        </div>

        {/* Bookmarks Section */}
        <div className="py-[15px]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-2xl">⚡</span>
              دسترسی ها
            </h2>
            <Button
              onClick={() => {
                if (showBookmarkForm) {
                  resetBookmarkForm()
                } else {
                  setShowBookmarkForm(true)
                }
              }}
              className="text-xs"
            >
              {showBookmarkForm ? 'بستن' : '+ افزودن'}
            </Button>
          </div>

          {/* Add/Edit Bookmark Form */}
          {showBookmarkForm && (
            <Card className="mb-4 p-4 space-y-3 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {editingId ? '✏️ ویرایش بوکمارک' : '➕ افزودن بوکمارک'}
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  عنوان (اختیاری)
                </label>
                <input
                  type="text"
                  value={bookmarkTitle}
                  onChange={(e) => setBookmarkTitle(e.target.value)}
                  placeholder="مثلاً: جیمیل"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL *
                </label>
                <input
                  type="url"
                  value={bookmarkUrl}
                  onChange={(e) => setBookmarkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  تصویر (اختیاری)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      const reader = new FileReader()
                      reader.onload = (evt) => setBookmarkImage(evt.target?.result as string)
                      reader.readAsDataURL(file)
                    }
                  }}
                  className="w-full text-sm text-gray-600 dark:text-gray-400"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveBookmark} className="flex-1 bg-green-600 hover:bg-green-700">
                  {editingId ? '✓ ذخیره' : '+ افزودن'}
                </Button>
                <Button
                  onClick={resetBookmarkForm}
                  className="flex-1 bg-gray-400 hover:bg-gray-500"
                >
                  لغو
                </Button>
              </div>
            </Card>
          )}

          {/* Bookmarks Grid */}
          {bookmarks.length > 0 ? (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))' }}>
              {bookmarks.map(bookmark => (
                <div
                  key={bookmark.id}
                  className="group relative flex flex-col items-center"
                >
                  {/* Bookmark Card */}
                  <div className="relative w-full h-20 bg-white dark:bg-slate-800 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-1 transition-all overflow-hidden flex items-center justify-center">
                    {/* Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-800"></div>

                    {/* Image */}
                    <button
                      onClick={() => window.open(bookmark.url, '_blank')}
                      className="relative z-10 flex items-center justify-center w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                      title={bookmark.title}
                    >
                      {bookmark.imageUrl ? (
                        <img
                          src={bookmark.imageUrl}
                          alt={bookmark.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : (
                        <img
                          src={getFaviconUrl(bookmark.url)}
                          alt={bookmark.title}
                          className="w-10 h-10 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      )}
                    </button>

                    {/* Fallback icon */}
                    <div className="absolute inset-0 flex items-center justify-center text-3xl hidden group-has-[img:hidden]:flex">
                      🔗
                    </div>

                    {/* Delete button - Enhanced UX */}
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleDeleteBookmark(bookmark.id)
                      }}
                      className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full text-sm flex items-center justify-center transition-all shadow-md hover:scale-110 z-20"
                      title="حذف بوکمارک"
                    >
                      ✕
                    </button>

                    {/* Edit button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleEditBookmark(bookmark.id)
                      }}
                      className="absolute -top-2 -left-2 opacity-0 group-hover:opacity-100 bg-blue-500 hover:bg-blue-600 text-white w-6 h-6 rounded-full text-sm flex items-center justify-center transition-all shadow-md hover:scale-110 z-20"
                      title="ویرایش بوکمارک"
                    >
                      ✏️
                    </button>
                  </div>

                  {/* Bookmark Title */}
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-2 text-center line-clamp-2 w-full px-1">
                    {bookmark.title}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon="search"
              title="بوکمارکی وجود ندارد"
              description="بوکمارک اول خود را اضافه کنید"
              action={{ label: 'افزودن بوکمارک', onClick: () => setShowBookmarkForm(true) }}
            />
          )}
        </div>

        {/* Weather Forecast */}
        {weatherLocations.length > 0 && (
          <div className="fade-in py-[15px]">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
              <span className="text-2xl">🌤️</span>
              پیش‌بینی آب‌وهوا
            </h2>
            
            {/* Grid: 3 cities per row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {weatherLocations.map((location) => {
                const data = weatherData[location.id]
                
                if (!data) {
                  return (
                    <div key={location.id} className="bg-white dark:bg-slate-800 rounded-lg p-4 h-32 shadow-sm dark:shadow-lg">
                      <div className="animate-pulse h-full">
                        <div className="h-4 bg-gray-300 dark:bg-slate-600 rounded w-24 mb-3"></div>
                        <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-32"></div>
                      </div>
                    </div>
                  )
                }

                const gradient = getConsistentGradient(location.id)

                return (
                  <div 
                    key={location.id} 
                    className={`
                      relative overflow-hidden rounded-lg shadow-sm dark:shadow-lg
                      transition-all duration-300 ease-out
                      hover:shadow-md dark:hover:shadow-xl hover:scale-105
                      group cursor-pointer
                      h-40 py-4 px-3
                    `}
                  >
                    {/* Animated Background Gradient */}
                    <div 
                      className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-15 
                        group-hover:opacity-25 transition-opacity duration-300`}
                    />
                    
                    {/* Glass Effect Background */}
                    <div className="absolute inset-0 bg-white dark:bg-slate-700 opacity-[0.08] 
                      dark:opacity-[0.12] group-hover:opacity-[0.15] dark:group-hover:opacity-[0.18] 
                      transition-opacity duration-300" />

                    {/* Content */}
                    <div className="relative flex items-stretch h-full">
                      {/* Left: City Name */}
                      <div className="flex items-center justify-center px-4 min-w-24">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white text-right">
                          {location.city}
                        </h3>
                      </div>

                      {/* Center: Current Weather */}
                      <div className="flex-1 flex items-center justify-center px-6">
                        <div className="text-center">
                          <p className="text-3xl font-bold text-gray-900 dark:text-white transition-transform duration-300 
                            group-hover:scale-110">
                            {Math.round(data.current.temp)}°C
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            {data.current.description}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            رطوبت: {data.current.humidity}%
                          </p>
                        </div>
                      </div>

                      {/* Right: Weather Icon + 3-Day Forecast */}
                      <div className="flex items-center px-4">
                        <div className="text-center">
                          <div className="text-4xl mb-3 transition-transform duration-300 
                            group-hover:scale-125 group-hover:rotate-12">
                            {getWeatherIcon(data.current.description)}
                          </div>
                          
                          {/* 3-Day Mini Forecast */}
                          <div className="flex gap-2">
                            {data.forecast.slice(0, 3).map((day, idx) => (
                              <div 
                                key={idx} 
                                className="bg-white dark:bg-slate-600 bg-opacity-10 dark:bg-opacity-20 
                                  rounded-lg p-1.5 min-w-16 text-center text-xs
                                  border border-white border-opacity-10 dark:border-slate-500 dark:border-opacity-20
                                  hover:bg-opacity-20 dark:hover:bg-opacity-30
                                  transition-all duration-200
                                  cursor-pointer"
                              >
                                <p className="text-gray-700 dark:text-gray-200 mb-0.5 text-xs whitespace-nowrap">
                                  {calendar === 'jalali' ? formatDateLocalized(new Date(day.date), 'jalali', language === 'fa' ? 'fa' : 'en') : formatDateLocalized(new Date(day.date), 'gregorian', language === 'fa' ? 'fa' : 'en')}
                                </p>
                                <p className="text-lg mb-0.5">{getWeatherIcon(day.description)}</p>
                                <p className="font-bold text-xs text-gray-800 dark:text-white">
                                  {Math.round(day.temp_max)}° / {Math.round(day.temp_min)}°
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Enhanced Tools Section - 4 Cards in One Row */}
        <div className="py-[15px]">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
            <span className="text-2xl">⚡</span>
            {language === 'fa' ? 'ابزارها' : 'Tools'}
          </h2>
          
          {/* 4 Tool Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {/* ✨ Motivational Quote - Enhanced Design */}
            <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 dark:from-slate-800 dark:via-purple-900/20 dark:to-slate-800 rounded-xl p-4 shadow-lg dark:shadow-xl flex flex-col h-80 overflow-hidden group hover:shadow-2xl transition-all duration-300 relative">
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white to-transparent opacity-10 group-hover:opacity-20 transition-opacity"></div>
              
              {/* Content */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="text-3xl animate-pulse">💫</div>
                    <div>
                      <p className="text-xs font-bold text-purple-600 dark:text-white/80 uppercase tracking-widest">الهام‌بخش</p>
                      <p className="text-purple-900 dark:text-white font-bold text-sm">{language === 'fa' ? 'نقل‌قول روز' : 'Daily Quote'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const randomIndex = Math.floor(Math.random() * motivationalQuotes.length)
                      setCurrentQuote(motivationalQuotes[randomIndex])
                    }}
                    className="text-purple-700 dark:text-white bg-white/20 hover:bg-white/40 dark:bg-black/20 dark:hover:bg-black/40 p-2.5 rounded-lg text-lg transition backdrop-blur-sm"
                    title={language === 'fa' ? 'نقل‌قول جدید' : 'New quote'}
                  >
                    🔄
                  </button>
                </div>

                {/* Quote Text */}
                <p className="italic text-purple-900 dark:text-white flex-1 overflow-y-auto text-center flex items-center justify-center leading-relaxed font-medium" style={{fontSize: '16px'}}>
                  "{language === 'fa' ? currentQuote.text_fa : currentQuote.text}"
                </p>

                {/* Author */}
                <p className="text-purple-700 dark:text-white/70 mt-3 text-center text-sm font-semibold">— {currentQuote.author}</p>
              </div>
            </div>

            {/* 📝 Quick Notes - Enhanced Design */}
            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-slate-800 dark:via-orange-900/20 dark:to-slate-800 rounded-xl p-4 shadow-lg dark:shadow-xl flex flex-col h-80 overflow-hidden group hover:shadow-2xl transition-all duration-300 relative">
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white to-transparent opacity-10 group-hover:opacity-20 transition-opacity"></div>

              <div className="relative z-10 flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-3xl">📝</div>
                    <div>
                      <p className="text-xs font-bold text-amber-600 dark:text-white/80 uppercase tracking-widest">یادداشت</p>
                      <p className="text-amber-900 dark:text-white font-bold text-sm">{language === 'fa' ? 'سریع و آسان' : 'Quick Notes'}</p>
                    </div>
                  </div>
                  <span className="text-xs bg-amber-900/20 dark:bg-white/30 backdrop-blur-sm text-amber-900 dark:text-white px-3 py-1 rounded-full font-bold">
                    {quickNotes.length}/5
                  </span>
                </div>

                {/* Notes List */}
                <div className="flex-1 min-h-[40px] space-y-1.5 mb-3 overflow-y-auto pr-1 custom-scrollbar">
                  {quickNotes.length === 0 ? (
                    <p className="text-amber-600 dark:text-white/60 text-xs italic text-center py-4">
                      {language === 'fa' ? 'یادداشتی ندارید' : 'No notes yet'}
                    </p>
                  ) : (
                    quickNotes.map((note, i) => (
                      <div key={i} className="flex items-start justify-between gap-2 bg-amber-900/20 dark:bg-white/30 backdrop-blur-sm hover:bg-amber-900/30 dark:hover:bg-white/40 p-2 rounded-lg text-xs text-amber-900 dark:text-white transition group/note">
                        <span className="line-clamp-2 flex-1">• {note}</span>
                        <button
                          onClick={() => {
                            const updated = quickNotes.filter((_, idx) => idx !== i)
                            setQuickNotes(updated)
                            localStorage.setItem('quickNotes', JSON.stringify(updated))
                          }}
                          className="text-amber-700 dark:text-white/60 hover:text-amber-900 dark:hover:text-white flex-shrink-0 text-xs opacity-0 group-hover/note:opacity-100 transition"
                          title={language === 'fa' ? 'حذف' : 'Delete'}
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Input */}
                <div className="flex gap-1.5 mt-auto pt-3 border-t border-amber-900/20 dark:border-white/20">
                  <input
                    type="text"
                    placeholder={language === 'fa' ? 'یادداشت...' : 'Add note...'}
                    id="quick-note-input"
                    className="flex-1 text-xs px-3 py-2 rounded-lg border-0 bg-amber-900/20 dark:bg-white/30 backdrop-blur-sm text-amber-900 dark:text-white placeholder-amber-700 dark:placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-amber-700 dark:focus:ring-white/60 transition"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                        const input = e.target as HTMLInputElement
                        const updated = [input.value, ...quickNotes].slice(0, 5)
                        setQuickNotes(updated)
                        localStorage.setItem('quickNotes', JSON.stringify(updated))
                        input.value = ''
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('quick-note-input') as HTMLInputElement
                      if (input && input.value.trim()) {
                        const updated = [input.value, ...quickNotes].slice(0, 5)
                        setQuickNotes(updated)
                        localStorage.setItem('quickNotes', JSON.stringify(updated))
                        input.value = ''
                      }
                    }}
                    className="px-3 py-2 rounded-lg bg-amber-900/30 dark:bg-white/40 hover:bg-amber-900/50 dark:hover:bg-white/60 text-amber-900 dark:text-white font-bold text-sm transition backdrop-blur-sm"
                    title={language === 'fa' ? 'افزودن' : 'Add'}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* 🎭 Persian Poetry - Enhanced Design */}
            <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-800 dark:via-indigo-900/20 dark:to-slate-800 rounded-xl p-4 shadow-lg dark:shadow-xl flex flex-col h-80 overflow-hidden group hover:shadow-2xl transition-all duration-300 relative">
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white to-transparent opacity-10 group-hover:opacity-20 transition-opacity"></div>

              <div className="relative z-10 flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-3xl flex-shrink-0">🎭</span>
                      <p className="text-xs font-bold text-indigo-600 dark:text-white/80 uppercase tracking-widest">شعر</p>
                    </div>
                    <p className="text-indigo-900 dark:text-white font-bold text-sm line-clamp-1">{currentPoem.title}</p>
                    <p className="text-xs text-indigo-700 dark:text-white/60 font-semibold line-clamp-1">{currentPoem.poet}</p>
                  </div>
                  <button
                    onClick={() => {
                      const randomIndex = Math.floor(Math.random() * persianPoems.length)
                      setCurrentPoem(persianPoems[randomIndex])
                      setShowPoemAnalysis(false)
                    }}
                    className="text-indigo-700 dark:text-white bg-white/20 hover:bg-white/40 dark:bg-black/20 dark:hover:bg-black/40 p-2.5 rounded-lg text-lg transition flex-shrink-0 backdrop-blur-sm ml-2"
                    title={language === 'fa' ? 'شعر جدید' : 'New poem'}
                  >
                    🔄
                  </button>
                </div>

                {/* Poem Content */}
                {!showPoemAnalysis ? (
                  <div className="text-indigo-900 dark:text-white flex-1 overflow-y-auto font-farsi pr-1 custom-scrollbar" style={{fontSize: '15px', lineHeight: '26px'}}>
                    <p className="whitespace-pre-wrap text-center">
                      {currentPoem.poem}
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-indigo-900 dark:text-white flex-1 overflow-y-auto bg-indigo-900/20 dark:bg-black/20 backdrop-blur-sm rounded-lg p-3 pr-1 custom-scrollbar leading-relaxed">
                    <p>{language === 'fa' ? currentPoem.analysis_fa : currentPoem.analysis_en}</p>
                  </div>
                )}

                {/* Toggle Button */}
                <button
                  onClick={() => setShowPoemAnalysis(!showPoemAnalysis)}
                  className={`mt-3 text-xs px-3 py-2 rounded-lg transition w-full font-bold transition-all duration-200 ${
                    showPoemAnalysis
                      ? 'bg-indigo-900/30 dark:bg-white/40 text-indigo-900 dark:text-white hover:bg-indigo-900/40 dark:hover:bg-white/50'
                      : 'bg-indigo-900/20 dark:bg-white/20 text-indigo-900 dark:text-white hover:bg-indigo-900/30 dark:hover:bg-white/30 backdrop-blur-sm'
                  }`}
                >
                  {language === 'fa' ? (showPoemAnalysis ? '📖 متن' : '📖 تحلیل') : (showPoemAnalysis ? '📖 Text' : '📖 Analysis')}
                </button>
              </div>
            </div>

            {/* Pomodoro Timer - Enhanced Modern Design */}
            <div className="bg-gradient-to-br from-red-50 via-orange-50 to-red-50 dark:from-slate-800 dark:via-red-900/20 dark:to-slate-800 rounded-lg p-4 shadow-md flex flex-col text-gray-800 dark:text-white h-80 overflow-hidden border border-red-100 dark:border-red-900/40">
              {/* Header with Status */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-2xl ${pomodoroIsBreak ? 'animate-bounce' : ''}`}>
                    {pomodoroIsBreak ? '☕' : '⏱️'}
                  </span>
                  <div>
                    <p className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      {pomodoroIsBreak
                        ? language === 'fa'
                          ? '☕ وقت استراحت'
                          : '☕ Break Time'
                        : language === 'fa'
                        ? '💪 وقت کار'
                        : '💪 Work Time'}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 font-bold">
                      {language === 'fa' ? englishToFarsiNumber(pomodoroSessionCount) : pomodoroSessionCount} {language === 'fa' ? 'جلسه' : 'sessions'}
                    </p>
                  </div>
                </div>
                <div className="text-2xl">
                  {pomodoroRunning ? '🔴' : '⚪'}
                </div>
              </div>

              {/* Circular Timer - More compact */}
              <div className="flex-1 flex items-center justify-center py-2">
                <div className="relative w-28 h-28">
                  {/* Background circle */}
                  <svg className="w-full h-full absolute" style={{transform: 'rotate(-90deg)'}}>
                    <circle
                      cx="56"
                      cy="56"
                      r="48"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      opacity="0.1"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="56"
                      cy="56"
                      r="48"
                      fill="none"
                      stroke={pomodoroIsBreak ? '#dc2626' : '#ef4444'}
                      strokeWidth="4"
                      strokeDasharray={`${(pomodoroTime / (pomodoroIsBreak ? 5 * 60 : 25 * 60)) * 301.6} 301.6`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  
                  {/* Timer display */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400 font-mono leading-none">
                      {Math.floor(pomodoroTime / 60).toString().padStart(2, '0')}<br/>
                      <span className="text-2xl">{(pomodoroTime % 60).toString().padStart(2, '0')}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="bg-white dark:bg-slate-700 rounded px-2 py-1 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">درحال</p>
                  <p className="text-sm font-bold text-gray-700 dark:text-white">
                    {language === 'fa' ? englishToFarsiNumber(pomodoroSessionCount) : pomodoroSessionCount}
                  </p>
                </div>
                <div className="bg-white dark:bg-slate-700 rounded px-2 py-1 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{pomodoroIsBreak ? 'استراحت' : 'کار'}</p>
                  <p className="text-sm font-bold text-gray-700 dark:text-white">
                    {pomodoroIsBreak ? '5:00' : '25:00'}
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex gap-2 mt-auto">
                <button
                  onClick={() => setPomodoroRunning(!pomodoroRunning)}
                  className="flex-1 py-2 rounded text-sm font-bold bg-red-500 hover:bg-red-600 text-white transition shadow-md"
                >
                  {pomodoroRunning ? '⏸️ ' : '▶️ '} {pomodoroRunning ? (language === 'fa' ? 'توقف' : 'Pause') : (language === 'fa' ? 'شروع' : 'Start')}
                </button>
                <button
                  onClick={() => {
                    setPomodoroTime(25 * 60)
                    setPomodoroRunning(false)
                    setPomodoroIsBreak(false)
                  }}
                  className="flex-1 py-2 rounded text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white transition shadow-md"
                >
                  🔄 {language === 'fa' ? 'تازه' : 'Reset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
